/* eslint-disable class-methods-use-this */
import React, { useReducer, useRef, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import { space, site, radii } from 'style/config';
import {
    Box, Text, Divider, Link, Flex
} from 'components/ui';
import LazyLoad from 'components/LazyLoad/LazyLoad';
import GalleryCard from 'components/Gallery/GalleryCard';
import SeeMoreCard from 'components/ProductPage/GalleryCardCarousel/SeeMoreCard/SeeMoreCard';
import ShadeFilter from 'components/ProductPage/ShadeFilter/ShadeFilter';
import languageLocale from 'utils/LanguageLocale';
import analyticsConsts from 'analytics/constants';
import communityUtils from 'utils/Community';
import GalleryInteractionActions from 'actions/GalleryInteractionActions';
import anaConsts from 'analytics/constants';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import Carousel from 'components/Carousel';
import pixleeUtils from 'utils/pixlee';
import urlUtils from 'utils/Url';
import GallerySelector from 'selectors/gallery/gallerySelector';
import GalleryCardCarouselEN from 'components/ProductPage/GalleryCardCarousel/locales/GalleryCardCarousel_en_US';
import GalleryCardCarouselFR from 'components/ProductPage/GalleryCardCarousel/locales/GalleryCardCarousel_fr_CA';
import { DebouncedResize } from 'constants/events';

const { gallerySelector } = GallerySelector;
const { COMMUNITY_URLS } = communityUtils;
const { getApprovedContentFromAlbum } = pixleeUtils;
const { getParamsByName } = urlUtils;
const BRANDS_WITHOUT_LOOKS = { CHANEL: '1065' };
const BI_TYPES_WITHOUT_LOOKS = ['birthday gift', 'rouge birthday gift', 'welcome kit'];
const SKU_TYPES_WITHOUT_LOOKS = ['sample', 'gwp', 'msg'];
const CARD_TRANSITION_OFFSET = 1;

const getText = text => languageLocale.getLocalisedDataFromResource(GalleryCardCarouselEN, GalleryCardCarouselFR)(text);

const containerPaddedMatches = () => window.matchMedia(`(min-width: ${site.containerMax + space.container * 2}px)`).matches;

function galleryReducer(state, action) {
    switch (action.type) {
        case 'UPDATE_MEDIAS':
            return { ...state, isFlush: action.isFlush, displayFilter: action.displayFilter, carouselContextId: action.contextId };
        case 'SET_FLUSH':
            return { ...state, isFlush: action.isFlush };
        case 'SET_FILTERS':
            return { ...state, selectedFilters: action.selectedFilters };
        default:
            return state;
    }
}

// eslint-disable-next-line complexity
const GalleryCardCarousel = ({
    productId, brandId, skuType, biType, currentProduct
}) => {
    const dispatch = useDispatch();
    const galleryItems = useSelector(state => gallerySelector(state)?.carouselGalleryData, shallowEqual) || [];

    const [state, localDispatch] = useReducer(galleryReducer, undefined, () => ({
        isFlush: null,
        selectedFilters: {},
        displayFilter: undefined,
        carouselContextId: JSON.stringify({ productId })
    }));

    const selectedFiltersRef = useRef(state.selectedFilters);
    selectedFiltersRef.current = state.selectedFilters;

    const isGift = biTypeProp => {
        if (biTypeProp) {
            return Object.values(BI_TYPES_WITHOUT_LOOKS).indexOf(biTypeProp) === -1;
        }

        return false;
    };

    const shouldDisplayGallery = () => {
        const isPpageBeautyBoardEnabled = Sephora.configurationSettings.productPageConfigurations.isPpageBeautyBoardEnabled;

        if (!isPpageBeautyBoardEnabled) {
            return false;
        }

        const displayForBrand = Object.values(BRANDS_WITHOUT_LOOKS).indexOf(brandId) === -1;
        const displayForType = SKU_TYPES_WITHOUT_LOOKS.indexOf(skuType.toLowerCase()) === -1;
        const shouldDisplay = displayForBrand && displayForType && !isGift(biType);

        if (shouldDisplay && typeof digitalData === 'object' && digitalData?.page?.attributes?.featureVariantKeys) {
            if (digitalData.page.attributes.featureVariantKeys.indexOf(analyticsConsts.PAGE_VARIANTS.EXPLORE_LOOKS) === -1) {
                digitalData.page.attributes.featureVariantKeys.push(analyticsConsts.PAGE_VARIANTS.EXPLORE_LOOKS);
            }
        }

        return shouldDisplay;
    };

    const updateMedias = (medias, displayFilter, contextId) => {
        localDispatch({
            type: 'UPDATE_MEDIAS',
            isFlush: !containerPaddedMatches(),
            displayFilter,
            contextId
        });
        dispatch(GalleryInteractionActions.setCarouselGallery(medias));
    };

    const fetchPixleeMedia = selectedSkuId => {
        const queryParamSkuId = getParamsByName('skuId')?.[0];
        const skuId = selectedSkuId || queryParamSkuId || currentProduct?.currentSku?.skuId;
        const options = {
            page: 1,
            photosPerPage: 30,
            // eslint-disable-next-line camelcase
            filters: { product_sku: { equals: skuId } }
        };
        getApprovedContentFromAlbum(Sephora.configurationSettings.exploreGalleryAlbumId, options)
            .then(({ data, album_id: albumId }) => {
                const filtersApplied = !!queryParamSkuId;
                const somethingFound = data?.length > 0;
                updateMedias(data, filtersApplied || somethingFound, albumId);
            })
            .catch(() => updateMedias([], false, skuId));
    };

    const fireAnalytics = newSelectedFilters => {
        const selections = Object.keys(newSelectedFilters).map(
            key => key.replace(/([A-Z])/g, ' $1').toLowerCase() + '=' + newSelectedFilters[key].join(',')
        );
        const skuAnalyticIndex = selections.findIndex(x => x.indexOf('sku id=') >= 0);

        if (skuAnalyticIndex >= 0) {
            const skus = (currentProduct.regularChildSkus || []).concat(currentProduct.onSaleChildSkus || []);
            const viewByColorValues = skus
                .filter(sku => newSelectedFilters['skuId'].find(value => value === sku.skuId))
                .map(sku => `${sku.variationValue} - ${sku?.refinements?.finishRefinements?.join(',') || ''}`);
            selections[skuAnalyticIndex] = `view by color=${viewByColorValues.join(',')}`;
        }

        const actionLink = 'see it in real life:filter';
        const filterSelections = selections.join('|');
        pdpAnalytics.trackLinkClick({
            actionInfo: actionLink,
            linkName: actionLink,
            filterSelections: filterSelections,
            selectedFilter: filterSelections,
            eventStrings: anaConsts.Event.EVENT_71
        });
    };

    const applyFilters = (filtersToApply = {}) => {
        const currentFilters = selectedFiltersRef.current;
        const filterKeys = Object.keys(filtersToApply);
        const newSelectedFilters = { ...currentFilters };

        for (const filterKey of filterKeys) {
            const filterValue = filtersToApply[filterKey];

            if (filterValue && filterValue.length) {
                newSelectedFilters[filterKey] = filterValue;
            } else {
                delete newSelectedFilters[filterKey];
            }
        }

        if (JSON.stringify(newSelectedFilters) !== JSON.stringify(currentFilters)) {
            localDispatch({ type: 'SET_FILTERS', selectedFilters: newSelectedFilters });
            fetchPixleeMedia(newSelectedFilters?.skuId ? newSelectedFilters?.skuId[0] : null);

            if (Object.keys(newSelectedFilters).length > 0) {
                fireAnalytics(newSelectedFilters);
            }
        }
    };

    useEffect(() => {
        const handleResize = () => localDispatch({ type: 'SET_FLUSH', isFlush: !containerPaddedMatches() });

        window.addEventListener(DebouncedResize, handleResize);
        fetchPixleeMedia();

        return () => window.removeEventListener(DebouncedResize, handleResize);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const prevProductIdRef = useRef(productId);
    useEffect(() => {
        if (prevProductIdRef.current !== productId) {
            prevProductIdRef.current = productId;
            fetchPixleeMedia();
        }
    }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!shouldDisplayGallery()) {
        return null;
    }

    const { displayFilter, isFlush, carouselContextId } = state;

    const items = [];
    galleryItems.forEach((media, index) => {
        items.push(
            <GalleryCard
                key={index}
                inCarousel={true}
                index={index}
                analyticsCardName={anaConsts.CARD_NAMES.UGC_IMAGE}
                {...media}
            />
        );
    });

    if (items.length === 30) {
        items.push(<SeeMoreCard style={styles.card} />);
    }

    const hasItems = items.length > 0;

    return (
        <Box
            data-at={Sephora.debug.dataAt('see_it_in_real_life_section')}
            paddingBottom={hasItems ? [6, 8] : [4, 5]}
            lineHeight='tight'
        >
            <Divider />
            <Flex
                flexDirection='column'
                gap={1}
            >
                <Text
                    data-at={Sephora.debug.dataAt('see_it_in_real_life_title')}
                    is='h2'
                    marginTop='1em'
                    fontSize={['md', 'lg']}
                    fontWeight='bold'
                    children={getText('seeItInRealLife')}
                />
                <p children={getText('mentionSephora')} />
                <div>
                    <Link
                        data-at={Sephora.debug.dataAt('add_your_photo_link')}
                        href={COMMUNITY_URLS.ADD_PHOTO}
                        color='blue'
                        children={getText(hasItems ? 'addYourPhoto' : 'addAPhoto')}
                    />
                </div>
            </Flex>
            {displayFilter && (
                <Flex
                    marginTop={3}
                    marginLeft={[-2, 0]}
                >
                    <ShadeFilter
                        currentProduct={currentProduct}
                        applyFilters={applyFilters}
                    />
                </Flex>
            )}
            {hasItems && (
                <Box
                    marginY={-CARD_TRANSITION_OFFSET}
                    paddingTop={[3, 4]}
                >
                    <LazyLoad
                        analyticsCarouselName={anaConsts.CAROUSEL_NAMES.SEE_IT_REAL}
                        contextId={carouselContextId}
                        dataAt='see_it_real_carousel'
                        component={Carousel}
                        gap={[1, 2]}
                        itemWidth={[136, 188]}
                        marginX={isFlush && '-container'}
                        scrollPadding={isFlush && 2}
                        items={items}
                        title={getText('seeItInRealLife')}
                    />
                </Box>
            )}
            {!hasItems && displayFilter && (
                <Text
                    dataAt={Sephora.debug.dataAt('no_images_see_it_real_carousel')}
                    is='h3'
                    marginTop={5}
                    marginBottom={3}
                    fontWeight='bold'
                    children={getText('sorryNoImages')}
                />
            )}
        </Box>
    );
};

const styles = {
    card: {
        borderRadius: radii[2],
        overflow: 'hidden',
        width: '100%',
        position: 'relative',
        paddingBottom: '100%'
    },
    cardHover: {
        borderRadius: radii[2],
        overflow: 'hidden',
        width: '100%',
        position: 'relative',
        paddingBottom: '100%',
        outline: 0,
        transition: 'transform .2s',
        marginTop: space[CARD_TRANSITION_OFFSET],
        '.no-touch &': {
            willChange: 'transform'
        },
        '.no-touch &:hover, :focus': {
            transform: `translateY(-${space[CARD_TRANSITION_OFFSET]}px)`
        }
    }
};

export default wrapFunctionalComponent(GalleryCardCarousel, 'GalleryCardCarousel');
